import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Shield, MapPin, Users, CheckCircle2, Home, Wallet, Star, Sparkles,
  Search, ArrowRight, Lock, Unlock, Phone, BadgeCheck, Flame, Award, X,
  Camera, Upload, Trash2, Fingerprint,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { photoUrl, uploadPhoto } from "../lib/photos";
import Onboarding49Card from "../components/Onboarding49Card";
import FlatmateBedsStrip from "../components/FlatmateBedsStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG =
  "https://customer-assets-eiarnc6j.emergentagent.net/wingman/05a3193c-9053-43cf-b79c-afd2faf51841/attachments/34e6a961ca6442f7bc59eb03b8b74d8f_file_000000000af08208b639e7d560c14967.png";
const TAGLINE = "Flat Hunting Made Easy. Better People. Better Homes. Brighter Living.";

const authHeaders = (token) => (token ? { Authorization: `Bearer ${token}` } : {});

const TRUST_PILLS = [
  { icon: Users, label: "Verified Flatmates", color: "#F97316" },
  { icon: Shield, label: "Trusted Helpers", color: "#10B981" },
  { icon: Home, label: "On-site Verification", color: "#EC4899" },
  { icon: MapPin, label: "Verified Locations", color: "#3B82F6" },
  { icon: Sparkles, label: "Multiple Options", color: "#8B5CF6" },
  { icon: BadgeCheck, label: "Safe & Reliable", color: "#F59E0B" },
];

function useFlatmateSettings() {
  const [s, setS] = useState(null);
  useEffect(() => {
    axios.get(`${API}/flatmate/settings`).then((r) => setS(r.data.settings)).catch(() => {});
  }, []);
  return s;
}

function Badge({ color, bg, label, icon: Icon }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5"
          style={{ color, background: bg, border: `1px solid ${color}33` }}>
      {Icon && <Icon className="w-3 h-3" />} {label}
    </span>
  );
}

// -------------------- Landing --------------------
export function FlatmateLanding() {
  const settings = useFlatmateSettings();
  const { user, token, loading } = useAuth();
  const nav = useNavigate();
  const [needsRole, setNeedsRole] = useState(false);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/flatmate/me`, { headers: authHeaders(token) })
      .then((r) => { if (!r.data.profile?.role) setNeedsRole(true); })
      .catch(() => {});
  }, [user]);

  const goCheck = () => {
    if (!user) return nav("/login?next=/flatmate/journey");
    nav("/flatmate/journey");
  };
  const goList = () => {
    if (!user) return nav("/login?next=/flatmate/list");
    nav("/flatmate/list");
  };
  const goBrowse = () => nav("/flatmate/browse");

  return (
    <div className="pb-16" data-testid="flatmate-landing">
      {needsRole && <RoleSelectorModal onDone={() => setNeedsRole(false)} />}

      {/* 3-TILE ENTRY — Rent, Flatmate, Owner/Broker (matches uploaded design) */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-6" data-testid="flatmate-three-tiles">
        <div className="grid md:grid-cols-3 gap-4 md:gap-5">
          {/* Tile 1 — Looking for a Rented Flat */}
          <button
            data-testid="tile-find-flat"
            onClick={() => nav("/flatmate/browse?intent=rent")}
            className="group text-left rounded-3xl overflow-hidden border p-6 md:p-7 card-lift transition"
            style={{ borderColor: "#F4511822",
                     background: "linear-gradient(180deg,#FFF3EA 0%,#FFE8D5 100%)", minHeight: 340 }}>
            <div className="text-6xl md:text-7xl mb-2">🏢</div>
            <h3 className="font-display text-2xl md:text-3xl font-bold leading-tight" style={{ color: "#00604A" }}>
              Looking for a<br /><span style={{ color: "#F45118" }}>Rented Flat</span>
            </h3>
            <p className="mt-2 text-sm" style={{ color: "#667085" }}>Find complete flats for rent</p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="rounded-xl bg-white/70 px-3 py-2 text-xs font-bold flex items-center gap-1.5">🏠 Owner</div>
              <div className="rounded-xl bg-white/70 px-3 py-2 text-xs font-bold flex items-center gap-1.5">🧑‍💼 Broker</div>
              <div className="rounded-xl bg-white/70 px-3 py-2 text-xs font-bold flex items-center gap-1.5">🛏 PG / Hostel</div>
              <div className="rounded-xl bg-white/70 px-3 py-2 text-xs font-bold flex items-center gap-1.5">🖥 Co-working</div>
            </div>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-white font-bold text-sm shadow-lg"
                 style={{ background: "#F45118" }}>
              Find My Flat →
            </div>
          </button>

          {/* Tile 2 — Looking for a Flatmate */}
          <button
            data-testid="tile-find-flatmate"
            onClick={() => nav("/flatmate/browse?intent=flatmate")}
            className="group text-left rounded-3xl overflow-hidden border p-6 md:p-7 card-lift transition"
            style={{ borderColor: "#00604A22",
                     background: "linear-gradient(180deg,#E8F7EF 0%,#D5F0DF 100%)", minHeight: 340 }}>
            <div className="text-6xl md:text-7xl mb-2">🧑‍🤝‍🧑</div>
            <h3 className="font-display text-2xl md:text-3xl font-bold leading-tight" style={{ color: "#00604A" }}>
              Looking for a<br /><span className="italic">Flatmate</span>
            </h3>
            <p className="mt-2 text-sm" style={{ color: "#667085" }}>Find a flatmate in a shared flat</p>
            <ul className="mt-4 space-y-1.5 text-xs">
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full" style={{ background: "#00604A" }} /> Verified profiles · Aadhaar linked</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full" style={{ background: "#00604A" }} /> Lifestyle match — food, gender, timings</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full" style={{ background: "#00604A" }} /> Zero brokerage, unlock only when interested</li>
            </ul>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-white font-bold text-sm shadow-lg"
                 style={{ background: "#00604A" }}>
              Find My Flatmate →
            </div>
          </button>

          {/* Tile 3 — List (Owner or Broker) */}
          <div
            data-testid="tile-list-property"
            className="rounded-3xl overflow-hidden border p-6 md:p-7 card-lift transition"
            style={{ borderColor: "#00604A22",
                     background: "linear-gradient(180deg,#EEF3FF 0%,#E3EEFF 100%)", minHeight: 340 }}>
            <div className="text-6xl md:text-7xl mb-2">🔑</div>
            <h3 className="font-display text-2xl md:text-3xl font-bold leading-tight" style={{ color: "#00604A" }}>
              List your<br /><span style={{ color: "#F45118" }}>Property</span>
            </h3>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button data-testid="tile-list-owner"
                      onClick={() => user ? nav("/flatmate/list?role=owner") : nav("/signup-quick?role=customer&next=/flatmate/list?role=owner")}
                      className="rounded-2xl border-2 p-3 text-left transition"
                      style={{ borderColor: "#00604A", background: "#fff" }}>
                <div className="text-xs font-bold rounded-full px-2 py-0.5 inline-block text-white" style={{ background: "#00604A" }}>Owner</div>
                <div className="mt-1.5 text-sm font-bold" style={{ color: "#00604A" }}>List Your Flat</div>
                <div className="text-[10px]" style={{ color: "#667085" }}>Post directly, no brokerage</div>
              </button>
              <button data-testid="tile-list-broker"
                      onClick={() => user ? nav("/flatmate/list?role=broker") : nav("/signup-quick?role=customer&next=/flatmate/list?role=broker")}
                      className="rounded-2xl border-2 p-3 text-left transition"
                      style={{ borderColor: "#F45118", background: "#fff" }}>
                <div className="text-xs font-bold rounded-full px-2 py-0.5 inline-block text-white" style={{ background: "#F45118" }}>Broker</div>
                <div className="mt-1.5 text-sm font-bold" style={{ color: "#F45118" }}>List Flats as Broker</div>
                <div className="text-[10px]" style={{ color: "#667085" }}>Zero brokerage for users</div>
              </button>
            </div>
            <div className="mt-4 rounded-xl px-3 py-2.5 bg-white/70 text-[11px]" style={{ color: "#667085" }}>
              <b style={{ color: "#00604A" }}>Quick post from image or link:</b> Paste a Facebook/Instagram/WhatsApp URL or upload up to 2 property photos — we'll auto-extract name, rent, city, amenities & create a listing draft in seconds.
            </div>
          </div>
        </div>
        <FlatmateBedsStrip />
      </div>

      {/* HERO */}
      <div className="w-full bg-gradient-to-b from-emerald-50 via-white to-white mt-4">
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-6 pb-2">
          <img src={HERO_IMG} alt="HeylpMATE Flatmate Assist"
               data-testid="flatmate-hero-img"
               className="w-full rounded-3xl shadow-lg" />
          <p className="mt-4 text-center font-display text-lg md:text-2xl" style={{ color: "#10B981" }}>
            {TAGLINE}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 -mt-2">
        <div className="flex flex-col md:flex-row items-stretch gap-3 mt-6">
          <button data-testid="cta-first-check" onClick={goCheck}
                  className="flex-1 rounded-2xl px-6 py-5 text-white font-bold text-base md:text-lg shadow-xl transition hover:scale-[1.02]"
                  style={{ background: "linear-gradient(135deg, #F97316, #EA580C)" }}>
            Get Your First Property Checked — ₹{settings?.first_check_price_inr || 99}
          </button>
          <button data-testid="cta-list-room" onClick={goList}
                  className="rounded-2xl px-6 py-5 font-bold text-sm md:text-base transition border border-black/10 bg-white hover:bg-neutral-50">
            List My Room
          </button>
          <button data-testid="cta-browse" onClick={goBrowse}
                  className="rounded-2xl px-6 py-5 font-bold text-sm md:text-base transition"
                  style={{ background: "#10B981", color: "white" }}>
            Browse Verified Flatmates
          </button>
        </div>
        <p className="text-center text-xs text-neutral-500 mt-3 leading-relaxed">
          Don't take heavy one-time subscriptions of ₹500 or ₹1000. Start small with HeylpMATE upfront and{" "}
          <b style={{ color: "#F97316" }}>save 75% cost</b>. Pay only for what you need.
        </p>
      </div>

      {/* TRUST PILLS */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 mt-10">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3" data-testid="trust-pills">
          {TRUST_PILLS.map((p) => (
            <div key={p.label} className="rounded-2xl border border-black/5 bg-white p-3 text-center card-lift">
              <div className="mx-auto w-10 h-10 rounded-full grid place-items-center text-white"
                   style={{ background: p.color }}>
                <p.icon className="w-5 h-5" />
              </div>
              <div className="text-[11px] font-bold mt-2 leading-tight">{p.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* POPULAR CITIES */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 mt-14">
        <div className="flex justify-between items-end mb-4">
          <div>
            <h2 className="font-display text-2xl md:text-3xl">Popular cities</h2>
            <p className="text-sm text-neutral-500">Tap a city to browse verified flatmates & rooms.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="cities-grid">
          {(settings?.cities || []).map((c) => (
            <Link key={c.slug} to={`/flatmate/browse?city=${c.slug}`}
                  data-testid={`city-${c.slug}`}
                  className="relative rounded-2xl overflow-hidden group h-32 card-lift">
              <img src={c.photo} alt={c.name}
                   className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                   onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1477587458883-47145ed94245?w=600"; }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-2 left-2 right-2">
                <div className="text-white font-bold text-sm">{c.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* HOW ₹99 JOURNEY WORKS */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 mt-16">
        <div className="rounded-3xl border border-emerald-200 bg-emerald-50/40 p-6 md:p-10">
          <div className="chip mb-3" style={{ borderColor: "#10B981", color: "#10B981" }}>
            <Flame className="w-3.5 h-3.5" /> The ₹99 Flatmate Journey
          </div>
          <h3 className="font-display text-2xl md:text-3xl">Not a membership. Not a subscription. Just verified peace.</h3>
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {[
              { n: 1, t: "Pay ₹99 via UPI", d: "Kick off with a single verified property check." },
              { n: 2, t: "Helper assigned in 24h", d: "Local HeylpMATE reaches out on WhatsApp." },
              { n: 3, t: "On-site visit & report", d: "Structured report — condition, owner, hidden charges." },
              { n: 4, t: "Admin approves report", d: "Listing earns the Verified Flat badge." },
              { n: 5, t: "Full report in dashboard", d: "You get every detail photos never showed." },
              { n: 6, t: "Optional upgrade", d: "DIY ₹449/30d or Relax ₹749/45d. Soft prompt, never forced." },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl bg-white border border-black/5 p-4">
                <div className="text-xs uppercase tracking-widest text-neutral-500">Step {s.n}</div>
                <div className="font-display text-lg mt-1" style={{ color: "#00604A" }}>{s.t}</div>
                <div className="text-sm text-neutral-600 mt-1">{s.d}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <button data-testid="journey-primary" onClick={goCheck}
                    className="rounded-2xl px-6 py-3 text-white font-bold shadow-lg"
                    style={{ background: "linear-gradient(135deg, #F97316, #EA580C)" }}>
              Start My ₹99 Check <ArrowRight className="inline w-4 h-4 ml-1" />
            </button>
          </div>
        </div>
      </div>

      {/* PLANS */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 mt-14">
        <h2 className="font-display text-2xl md:text-3xl">Membership plans</h2>
        <p className="text-sm text-neutral-500">Optional — kick in only if you want unlimited access.</p>
        <div className="grid md:grid-cols-2 gap-4 mt-5">
          {(settings?.plans || []).map((p) => (
            <div key={p.slug} className="rounded-3xl border border-black/10 bg-white p-6 card-lift" data-testid={`plan-${p.slug}`}>
              <div className="flex items-center justify-between">
                <div className="font-display text-xl" style={{ color: "#00604A" }}>{p.name}</div>
                <div className="font-display text-2xl" style={{ color: "#10B981" }}>₹{p.price_inr}</div>
              </div>
              <div className="text-xs text-neutral-500 mt-1">{p.duration_days} days</div>
              <ul className="mt-3 space-y-1.5 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-none mt-0.5" /> {f}</li>
                ))}
              </ul>
              <div className="text-[11px] text-neutral-500 mt-3">{p.refund}</div>
              <Link to={`/flatmate/pay?plan=${p.slug}`} data-testid={`plan-buy-${p.slug}`}
                    className="mt-4 inline-block rounded-full px-5 py-2 font-bold text-sm text-white"
                    style={{ background: "linear-gradient(135deg, #10B981, #00604A)" }}>
                Buy {p.name}
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* CREDITS */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 mt-14">
        <h2 className="font-display text-2xl md:text-3xl">Flatmate Credits</h2>
        <p className="text-sm text-neutral-500">Recharge once — spend as you go. Valid 6 months.</p>
        <div className="grid md:grid-cols-3 gap-3 mt-5">
          {(settings?.credit_packs || []).map((p) => (
            <div key={p.slug} className="rounded-2xl border border-black/10 bg-white p-5" data-testid={`credit-${p.slug}`}>
              <div className="font-display text-xl">{p.name}</div>
              <div className="text-3xl font-display mt-2" style={{ color: "#10B981" }}>₹{p.price_inr}</div>
              <div className="text-sm text-neutral-500">{p.credits} credits</div>
              <Link to={`/flatmate/pay?pack=${p.slug}`} data-testid={`credit-buy-${p.slug}`}
                    className="mt-3 inline-flex items-center gap-1 text-sm font-bold" style={{ color: "#00604A" }}>
                <Wallet className="w-4 h-4" /> Recharge
              </Link>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-2xl border border-dashed border-black/10 p-4 text-xs text-neutral-600">
          <b>Credit costs:</b> Unlock contact 2 · Site visit 8 · Property report 5 · Tenant verification 10 · ₹99 first check = 10 credits.
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 mt-14 text-center">
        <Link to="/flatmate/refunds" data-testid="refunds-link"
              className="text-xs text-neutral-500 underline underline-offset-4">
          Refund & cancellation policy
        </Link>
      </div>
    </div>
  );
}

// -------------------- Role Selector --------------------
function RoleSelectorModal({ onDone }) {
  const { user, token, loading } = useAuth();
  const [busy, setBusy] = useState(false);
  const pick = async (role) => {
    setBusy(true);
    try {
      await axios.post(`${API}/flatmate/me`, { role }, { headers: authHeaders(token) });
      toast.success("Saved");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[9997] bg-black/50 grid place-items-center px-5" data-testid="role-selector">
      <div className="w-full max-w-lg rounded-3xl bg-white p-6 md:p-8 shadow-2xl">
        <div className="chip mb-2" style={{ borderColor: "#10B981", color: "#10B981" }}>
          <Home className="w-3.5 h-3.5" /> Welcome
        </div>
        <h2 className="font-display text-2xl md:text-3xl">What brings you here?</h2>
        <div className="grid grid-cols-2 gap-3 mt-5">
          <button data-testid="role-lister" disabled={busy} onClick={() => pick("lister")}
                  className="rounded-2xl border border-black/10 p-5 hover:bg-emerald-50 transition text-left">
            <div className="text-3xl">🏠</div>
            <div className="font-bold mt-2">I have a room to share</div>
            <div className="text-xs text-neutral-500 mt-1">List your flat / room</div>
          </button>
          <button data-testid="role-seeker" disabled={busy} onClick={() => pick("seeker")}
                  className="rounded-2xl border border-black/10 p-5 hover:bg-blue-50 transition text-left">
            <div className="text-3xl">🔎</div>
            <div className="font-bold mt-2">I am looking for a place</div>
            <div className="text-xs text-neutral-500 mt-1">Browse verified rooms</div>
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------- Lifestyle Preferences --------------------
export function FlatmateOnboard() {
  const { user, token, loading } = useAuth();
  const nav = useNavigate();
  const settings = useFlatmateSettings();
  const [selected, setSelected] = useState([]);
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/flatmate/onboard"); return; }
    axios.get(`${API}/flatmate/me`, { headers: authHeaders(token) })
      .then((r) => { setSelected(r.data.profile?.lifestyle || []); setCity(r.data.profile?.city || ""); })
      .catch(() => {});
  }, [user, loading, token, nav]);

  const toggle = (slug) => setSelected((s) => s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]);

  const save = async () => {
    if (selected.length < 5) return toast.error("Select at least 5 preferences");
    setBusy(true);
    try {
      await axios.post(`${API}/flatmate/me`, { lifestyle: selected, city }, { headers: authHeaders(token) });
      toast.success("Preferences saved");
      nav("/flatmate/browse");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-10" data-testid="flatmate-onboard">
      <h1 className="font-display text-3xl md:text-4xl">Your lifestyle</h1>
      <p className="text-sm text-neutral-500 mt-1">Pick at least 5 — we use these to find compatible flatmates.</p>

      <div className="mt-6">
        <label className="text-xs uppercase tracking-widest text-neutral-500">City</label>
        <select value={city} onChange={(e) => setCity(e.target.value)}
                data-testid="onboard-city"
                className="mt-1 w-full rounded-2xl border border-black/10 p-3 bg-white">
          <option value="">Select a city…</option>
          {(settings?.cities || []).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mt-6" data-testid="lifestyle-grid">
        {(settings?.lifestyle_tags || []).map((t) => {
          const on = selected.includes(t.slug);
          return (
            <button key={t.slug} onClick={() => toggle(t.slug)}
                    data-testid={`tag-${t.slug}`}
                    className={`rounded-2xl border p-4 text-center transition ${on ? "bg-emerald-50 border-emerald-500 ring-2 ring-emerald-500" : "border-black/10 bg-white hover:border-black/20"}`}>
              <div className="text-3xl">{t.emoji}</div>
              <div className="text-xs font-bold mt-1">{t.label}</div>
            </button>
          );
        })}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <div className="text-sm text-neutral-500">{selected.length}/5 minimum</div>
        <button data-testid="onboard-save" onClick={save} disabled={busy || selected.length < 5}
                className="btn-primary px-6 py-3 rounded-full text-white font-bold disabled:opacity-50">
          {busy ? "Saving…" : "Save & Continue"}
        </button>
      </div>
    </div>
  );
}

// -------------------- Browse --------------------
export function FlatmateBrowse() {
  const [params, setParams] = useSearchParams();
  const { user, token } = useAuth();
  const settings = useFlatmateSettings();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    city: params.get("city") || "",
    min_rent: "",
    max_rent: "",
    room_type: "",
    furnished: "",
    gender: "",
  });

  const load = () => {
    setLoading(true);
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
    axios.get(`${API}/flatmate/listings?${q.toString()}`, { headers: authHeaders(token) })
      .then((r) => setItems(r.data.listings || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filters.city]); // eslint-disable-line
  useEffect(() => { if (filters.city) setParams({ city: filters.city }); }, [filters.city]); // eslint-disable-line

  const applyFilters = () => load();

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="flatmate-browse">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl">Verified rooms & flatmates</h1>
        <Link to="/flatmate/list" className="chip"><Home className="w-3.5 h-3.5" /> List my room</Link>
      </div>

      <div className="mt-4">
        <Onboarding49Card compact />
      </div>
      <FlatmateBedsStrip city={filters.city} />

      <div className="rounded-2xl border border-black/10 bg-white p-4 mt-4 grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
        <select data-testid="filter-city" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} className="rounded-xl border border-black/10 p-2 text-sm">
          <option value="">All cities</option>
          {(settings?.cities || []).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <input data-testid="filter-min-rent" placeholder="Min ₹" value={filters.min_rent} onChange={(e) => setFilters({ ...filters, min_rent: e.target.value })} className="rounded-xl border border-black/10 p-2 text-sm" />
        <input data-testid="filter-max-rent" placeholder="Max ₹" value={filters.max_rent} onChange={(e) => setFilters({ ...filters, max_rent: e.target.value })} className="rounded-xl border border-black/10 p-2 text-sm" />
        <select data-testid="filter-room" value={filters.room_type} onChange={(e) => setFilters({ ...filters, room_type: e.target.value })} className="rounded-xl border border-black/10 p-2 text-sm">
          <option value="">Room</option>
          <option value="single">Single</option>
          <option value="shared">Shared</option>
          <option value="entire_flat">Entire flat</option>
        </select>
        <select data-testid="filter-furnished" value={filters.furnished} onChange={(e) => setFilters({ ...filters, furnished: e.target.value })} className="rounded-xl border border-black/10 p-2 text-sm">
          <option value="">Furnished</option>
          <option value="fully">Fully</option>
          <option value="semi">Semi</option>
          <option value="unfurnished">Unfurnished</option>
        </select>
        <button data-testid="apply-filters" onClick={applyFilters} className="btn-primary rounded-xl text-white p-2 text-sm inline-flex items-center justify-center gap-1">
          <Search className="w-3.5 h-3.5" /> Apply
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="listings-grid">
        {loading && <div className="col-span-3 text-neutral-500">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="col-span-3 text-center py-16 text-neutral-500">
            No listings yet. <Link to="/flatmate/list" className="text-emerald-600 underline">Be the first to list</Link>.
          </div>
        )}
        {items.map((l) => <ListingCard key={l.id} l={l} />)}
      </div>
    </div>
  );
}

function ListingCard({ l }) {
  const nav = useNavigate();
  const src = l.source || "user";
  const isAdmin = src === "admin" || String(src).startsWith("partner:");
  return (
    <div className="rounded-2xl border border-black/10 bg-white overflow-hidden card-lift cursor-pointer"
         data-testid={`listing-card-${l.id}`}
         onClick={() => nav(`/flatmate/listing/${l.id}`)}>
      <div className="relative h-40 bg-gradient-to-br from-emerald-100 to-blue-100 overflow-hidden">
        {l.photos?.[0] ? (
          <img src={photoUrl(l.photos[0]) || l.photos[0]} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center text-neutral-400"><Home className="w-10 h-10" /></div>
        )}
        <div className="absolute top-2 left-2 flex flex-wrap gap-1">
          {isAdmin && <span className="hm-free-badge inline-flex items-center gap-1 text-[10px] font-black rounded-full px-2 py-0.5 text-white shadow-lg"
                            style={{ background: "linear-gradient(90deg,#F59E0B,#EF4444,#F59E0B)", backgroundSize: "200% 100%" }}>
            ✨ Free Listing
          </span>}
          {l.great_deal && <Badge label="Great Deal" color="#EA580C" bg="#FFEDD5" icon={Flame} />}
          {!isAdmin && l.featured && <Badge label="Featured" color="#F97316" bg="#FEF3C7" icon={Flame} />}
          {l.trusted && <Badge label="Trusted HeylpMATE" color="#10B981" bg="#D1FAE5" icon={Shield} />}
          {l.verified_flat && <Badge label="Verified Flat" color="#00604A" bg="#DBEAFE" icon={BadgeCheck} />}
        </div>
        {l.match_pct > 0 && (
          <div className="absolute top-2 right-2 bg-white/95 rounded-full text-[11px] font-bold px-2 py-0.5 shadow">
            {l.match_pct}% match
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex justify-between items-start">
          <div>
            <div className="font-display text-base capitalize">{l.property_type} · {(l.room_type || "").replace("_", " ") || "Details on Request"}</div>
            <div className="text-xs text-neutral-500 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {l.locality || "Details on Request"}, {l.city}
            </div>
          </div>
          <div className="font-display text-xl" style={{ color: "#10B981" }}>
            {l.rent_inr ? `₹${l.rent_inr}` : <span className="text-xs font-sans text-neutral-500">On Request</span>}
          </div>
        </div>
        <div className="text-xs mt-2 text-neutral-600 line-clamp-2">{l.description || "Details on Request"}</div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px] chip">{l.furnished || "—"}</span>
          {l.unlocked ? (
            <span className="text-[11px] inline-flex items-center gap-1 text-emerald-600 font-bold">
              <Unlock className="w-3 h-3" /> Unlocked
            </span>
          ) : (
            <span className="text-[11px] inline-flex items-center gap-1 text-neutral-500">
              <Lock className="w-3 h-3" /> {isAdmin ? "Request contact" : "Contact locked"}
            </span>
          )}
        </div>
        {!l.unlocked && (
          <div className="mt-3 rounded-xl px-3 py-2 text-[11px] flex items-center justify-between gap-2 relative overflow-hidden"
               style={{ background: "linear-gradient(90deg,#00604A,#0B7A5A,#F45118)", color: "#fff" }}
               data-testid={`diy-ticker-${l.id}`}
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 font-bold whitespace-nowrap animate-marquee">
              <span>✨ DIY &amp; Relax Plan</span>
              <span className="opacity-70">·</span>
              <span>Best Deal</span>
              <span className="opacity-70">·</span>
              <span>Zero brokerage</span>
              <span className="opacity-70">·</span>
              <span>Verified in 24h</span>
              <span className="opacity-70">·</span>
              <span>Skip visits · we do the check</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------- Listing Detail --------------------
export function FlatmateListingDetail() {
  const { lid } = useParams();
  const { user, token, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/flatmate/listings/${lid}`, { headers: authHeaders(token) })
      .then((r) => setData(r.data));
  };
  useEffect(load, [lid]); // eslint-disable-line

  const unlock = async () => {
    if (!user) return nav(`/login?next=/flatmate/listing/${lid}`);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/flatmate/listings/${lid}/unlock`, {}, { headers: authHeaders(token) });
      if (r.data.queued) {
        toast.success("Request sent — you'll be notified on WhatsApp once admin approves.");
      } else {
        toast.success("Contact unlocked");
      }
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402) {
        toast.error(d || "Insufficient credits");
        nav("/flatmate/pay?pack=starter");
      } else toast.error(d || "Failed");
    } finally { setBusy(false); }
  };

  if (!data) return <div className="max-w-4xl mx-auto p-8 text-neutral-500">Loading…</div>;
  const l = data.listing;
  const src = l.source || "user";
  const isAdmin = src === "admin" || String(src).startsWith("partner:");

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8" data-testid="listing-detail">
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          {l.photos && l.photos.length > 0 ? (
            <div className="space-y-2">
              <div className="rounded-3xl overflow-hidden h-80">
                <img src={photoUrl(l.photos[0]) || l.photos[0]} className="w-full h-full object-cover" alt="" />
              </div>
              {l.photos.length > 1 && (
                <div className="grid grid-cols-2 gap-2">
                  {l.photos.slice(1).map((p, i) => (
                    <img key={i} src={photoUrl(p) || p} className="w-full h-32 object-cover rounded-2xl" alt="" />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-3xl bg-gradient-to-br from-emerald-100 to-blue-100 h-80 overflow-hidden grid place-items-center text-neutral-400">
              <Home className="w-16 h-16" />
            </div>
          )}
        </div>
        <div>
          <div className="flex flex-wrap gap-2">
            {isAdmin && <span className="hm-free-badge inline-flex items-center gap-1 text-[11px] font-black rounded-full px-3 py-1 text-white shadow-lg"
                              style={{ background: "linear-gradient(90deg,#F59E0B,#EF4444,#F59E0B)", backgroundSize: "200% 100%" }}>
              ✨ Free Listing
            </span>}
            {l.great_deal && <Badge label="Great Deal" color="#EA580C" bg="#FFEDD5" icon={Flame} />}
            {!isAdmin && l.featured && <Badge label="Featured" color="#F97316" bg="#FEF3C7" icon={Flame} />}
            {l.trusted && <Badge label="Trusted HeylpMATE Listing" color="#10B981" bg="#D1FAE5" icon={Shield} />}
            {l.verified_flat && <Badge label="Verified Flat" color="#00604A" bg="#DBEAFE" icon={BadgeCheck} />}
          </div>
          <h1 className="font-display text-3xl mt-3 capitalize">{l.property_type} · {l.room_type?.replace("_", " ")}</h1>
          <div className="text-sm text-neutral-500 flex items-center gap-1 mt-1"><MapPin className="w-4 h-4" /> {l.locality}, {l.city}</div>
          <div className="font-display text-4xl mt-4" style={{ color: "#10B981" }}>₹{l.rent_inr}<span className="text-sm text-neutral-500 font-sans">/month</span></div>
          <div className="text-xs text-neutral-500 mt-1">Deposit ₹{l.deposit_inr} · Available {l.available_from}</div>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {(l.lifestyle_prefs || []).map((t) => <span key={t} className="chip text-[10px] capitalize">{t.replace("_", " ")}</span>)}
          </div>
          <div className="mt-6 p-4 rounded-2xl border border-black/10 bg-white">
            {data.unlocked ? (
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">Owner contact</div>
                <div className="font-display text-xl">{l.owner_name}</div>
                <a href={`tel:${l.mobile}`} className="mt-1 inline-flex items-center gap-1 text-emerald-700 font-bold">
                  <Phone className="w-4 h-4" /> {l.mobile}
                </a>
              </div>
            ) : (
              <>
                <div className="text-xs text-neutral-500">
                  {isAdmin ? "Contact hidden — request approval to unlock" : "Contact details locked"}
                </div>
                <button data-testid="unlock-btn" onClick={unlock} disabled={busy}
                        className="mt-2 btn-primary rounded-full text-white px-5 py-2 inline-flex items-center gap-1 disabled:opacity-50">
                  <Unlock className="w-4 h-4" /> {busy ? "…" : isAdmin ? "Request contact (2 credits on approval)" : "Unlock (2 credits)"}
                </button>
                {isAdmin && (
                  <div className="text-[11px] text-neutral-500 mt-2">
                    Admin reviews & approves — you'll get WhatsApp when contact is revealed.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <div className="mt-6 rounded-2xl border border-black/10 bg-white p-5">
        <h3 className="font-display text-xl">About this place</h3>
        <p className="text-sm text-neutral-700 mt-2 whitespace-pre-wrap">{l.description}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {(l.amenities || []).map((a) => <span key={a} className="chip text-[10px]">{a}</span>)}
        </div>
      </div>
    </div>
  );
}

// -------------------- List a Room --------------------
export function FlatmateList() {
  const { user, token, loading } = useAuth();
  const nav = useNavigate();
  const settings = useFlatmateSettings();
  const [form, setForm] = useState({
    property_type: "flat", city: "", locality: "", rent_inr: 0, deposit_inr: 0,
    room_type: "single", furnished: "semi", available_from: "",
    preferred_tenant: "any", tenant_type: "any",
    lifestyle_prefs: [], amenities: [], highlights: [],
    mobile_public: false, description: "", photos: [],
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!loading && !user) nav("/login?next=/flatmate/list"); }, [user, loading, nav]);

  const toggleArr = (key, val) => setForm((f) => ({
    ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.city || !form.locality || !form.rent_inr) return toast.error("Fill required fields");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/flatmate/listings`, form, { headers: authHeaders(token) });
      toast.success("Listing published");
      nav(`/flatmate/listing/${r.data.id}`);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const AMENITIES = ["Wi-Fi", "AC", "Parking", "Laundry", "Geyser", "Fridge"];
  const HIGHLIGHTS = ["Working professional", "College student", "25+ age", "Under 25",
    "Have 2-wheeler", "Have 4-wheeler", "Will shift immediately", "Have pets",
    "Need no furnishing", "Pure vegetarian"];

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-10" data-testid="flatmate-list-form">
      <h1 className="font-display text-3xl">List my room</h1>
      <p className="text-sm text-neutral-500 mt-1">Contact stays hidden until seekers unlock or take a plan.</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className="rounded-xl border border-black/10 p-3">
            <option value="flat">Flat</option><option value="room">Room</option>
            <option value="pg">PG</option><option value="studio">Studio</option>
          </select>
          <select value={form.room_type} onChange={(e) => setForm({ ...form, room_type: e.target.value })} className="rounded-xl border border-black/10 p-3">
            <option value="single">Single</option><option value="shared">Shared</option><option value="entire_flat">Entire flat</option>
          </select>
          <select value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-xl border border-black/10 p-3" required>
            <option value="">City *</option>
            {(settings?.cities || []).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <input required placeholder="Locality *" value={form.locality} onChange={(e) => setForm({ ...form, locality: e.target.value })} className="rounded-xl border border-black/10 p-3" />
          <input required type="number" placeholder="Monthly rent ₹ *" value={form.rent_inr || ""} onChange={(e) => setForm({ ...form, rent_inr: parseInt(e.target.value || "0") })} className="rounded-xl border border-black/10 p-3" />
          <input type="number" placeholder="Deposit ₹" value={form.deposit_inr || ""} onChange={(e) => setForm({ ...form, deposit_inr: parseInt(e.target.value || "0") })} className="rounded-xl border border-black/10 p-3" />
          <select value={form.furnished} onChange={(e) => setForm({ ...form, furnished: e.target.value })} className="rounded-xl border border-black/10 p-3">
            <option value="fully">Fully furnished</option><option value="semi">Semi</option><option value="unfurnished">Unfurnished</option>
          </select>
          <input type="date" value={form.available_from} onChange={(e) => setForm({ ...form, available_from: e.target.value })} className="rounded-xl border border-black/10 p-3" />
          <select value={form.preferred_tenant} onChange={(e) => setForm({ ...form, preferred_tenant: e.target.value })} className="rounded-xl border border-black/10 p-3">
            <option value="any">Any gender</option><option value="male">Male</option><option value="female">Female</option>
          </select>
          <select value={form.tenant_type} onChange={(e) => setForm({ ...form, tenant_type: e.target.value })} className="rounded-xl border border-black/10 p-3">
            <option value="any">Any tenant</option><option value="working">Working</option><option value="student">Student</option>
          </select>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Ideal flatmate lifestyle</div>
          <div className="flex flex-wrap gap-2">
            {(settings?.lifestyle_tags || []).map((t) => (
              <button type="button" key={t.slug} onClick={() => toggleArr("lifestyle_prefs", t.slug)}
                      className={`chip ${form.lifestyle_prefs.includes(t.slug) ? "bg-emerald-50 border-emerald-500 text-emerald-700" : ""}`}>
                {t.emoji} {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Amenities</div>
          <div className="flex flex-wrap gap-2">
            {AMENITIES.map((a) => (
              <button type="button" key={a} onClick={() => toggleArr("amenities", a)}
                      className={`chip ${form.amenities.includes(a) ? "bg-blue-50 border-blue-500 text-blue-700" : ""}`}>{a}</button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Property highlights</div>
          <div className="flex flex-wrap gap-2">
            {HIGHLIGHTS.map((h) => (
              <button type="button" key={h} onClick={() => toggleArr("highlights", h)}
                      className={`chip ${form.highlights.includes(h) ? "bg-emerald-50 border-emerald-500 text-emerald-700" : ""}`}>{h}</button>
            ))}
          </div>
        </div>

        <textarea placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full rounded-xl border border-black/10 p-3" rows="4" />

        <ListingPhotoPicker token={token}
                            photos={form.photos}
                            onChange={(photos) => setForm({ ...form, photos })} />

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.mobile_public} onChange={(e) => setForm({ ...form, mobile_public: e.target.checked })} />
          Make my mobile publicly visible
        </label>
        <button data-testid="submit-listing" type="submit" disabled={busy}
                className="btn-primary rounded-full text-white px-6 py-3 font-bold disabled:opacity-50">
          {busy ? "Publishing…" : "Publish listing"}
        </button>
      </form>
    </div>
  );
}

// -------------------- Journey (₹99) --------------------
export function FlatmateJourney() {
  const { user, token, loading } = useAuth();
  const nav = useNavigate();
  const settings = useFlatmateSettings();
  const [form, setForm] = useState({ city: "", area: "", budget_inr: 0, preference: "", shortlist_url: "", utr: "" });
  const [busy, setBusy] = useState(false);
  const [payMode, setPayMode] = useState("upi");

  useEffect(() => { if (!loading && !user) nav("/login?next=/flatmate/journey"); }, [user, loading, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.city || !form.area || !form.budget_inr) return toast.error("Fill all fields");
    if (payMode === "upi" && !form.utr) return toast.error("Enter UPI transaction reference (UTR)");
    setBusy(true);
    try {
      const payload = { ...form, utr: payMode === "upi" ? form.utr : "" };
      await axios.post(`${API}/flatmate/journey/start`, payload, { headers: authHeaders(token) });
      toast.success(payMode === "upi" ? "Payment submitted — admin will verify shortly" : "Journey started — helper assignment within 24h");
      nav("/flatmate/dashboard");
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      toast.error(d || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-10" data-testid="journey-form">
      <h1 className="font-display text-3xl">Get your first property checked — ₹{settings?.first_check_price_inr || 99}</h1>
      <p className="text-sm text-neutral-500 mt-1">A local HeylpMATE helper visits the flat & delivers a verified report in 24-72 hours.</p>
      <form onSubmit={submit} className="mt-6 space-y-3">
        <select required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full rounded-xl border border-black/10 p-3">
          <option value="">City *</option>
          {(settings?.cities || []).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <input required placeholder="Area / locality *" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className="w-full rounded-xl border border-black/10 p-3" />
        <input required type="number" placeholder="Budget ₹ *" value={form.budget_inr || ""} onChange={(e) => setForm({ ...form, budget_inr: parseInt(e.target.value || "0") })} className="w-full rounded-xl border border-black/10 p-3" />
        <input placeholder="Any preference (facing, floor, veg-only…)" value={form.preference} onChange={(e) => setForm({ ...form, preference: e.target.value })} className="w-full rounded-xl border border-black/10 p-3" />
        <input placeholder="Shortlist link (Magicbricks / 99acres / photo) — optional" value={form.shortlist_url} onChange={(e) => setForm({ ...form, shortlist_url: e.target.value })} className="w-full rounded-xl border border-black/10 p-3" />

        <div className="rounded-2xl border border-black/10 bg-white p-4">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Payment</div>
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={() => setPayMode("upi")} className={`chip ${payMode === "upi" ? "bg-emerald-50 border-emerald-500 text-emerald-700" : ""}`}>UPI (₹{settings?.first_check_price_inr || 99})</button>
            <button type="button" onClick={() => setPayMode("credits")} className={`chip ${payMode === "credits" ? "bg-emerald-50 border-emerald-500 text-emerald-700" : ""}`}>Wallet ({settings?.action_costs?.first_property_check_99 || 10} credits)</button>
          </div>
          {payMode === "upi" && (
            <div className="mt-3 text-sm space-y-2">
              <div>Pay ₹{settings?.first_check_price_inr || 99} to UPI <b>{settings?.upi_id || "heylpmate@upi"}</b></div>
              {settings?.upi_qr_url && <img src={settings.upi_qr_url} alt="UPI QR" className="w-40 h-40 rounded-xl" />}
              <input required placeholder="Enter UPI transaction reference (UTR)" value={form.utr} onChange={(e) => setForm({ ...form, utr: e.target.value })} className="w-full rounded-xl border border-black/10 p-3" />
            </div>
          )}
        </div>

        <button data-testid="journey-submit" type="submit" disabled={busy}
                className="w-full rounded-full text-white px-6 py-3 font-bold shadow-lg"
                style={{ background: "linear-gradient(135deg, #F97316, #EA580C)" }}>
          {busy ? "Submitting…" : "Start My ₹99 Journey"}
        </button>
      </form>
    </div>
  );
}

// -------------------- Payments (credits or plan) --------------------
export function FlatmatePay() {
  const [params] = useSearchParams();
  const { user, token, loading } = useAuth();
  const nav = useNavigate();
  const settings = useFlatmateSettings();
  const packSlug = params.get("pack");
  const planSlug = params.get("plan");
  const [utr, setUtr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!loading && !user) nav(`/login?next=/flatmate/pay?${params.toString()}`); }, [user, loading, params, nav]);

  const item = useMemo(() => {
    if (!settings) return null;
    if (packSlug) return { kind: "pack", ...settings.credit_packs.find((p) => p.slug === packSlug) };
    if (planSlug) return { kind: "plan", ...settings.plans.find((p) => p.slug === planSlug) };
    return null;
  }, [settings, packSlug, planSlug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!utr) return toast.error("Enter UPI transaction reference");
    setBusy(true);
    try {
      const url = item.kind === "pack" ? `${API}/flatmate/credits/recharge` : `${API}/flatmate/plans/buy`;
      const body = item.kind === "pack" ? { pack_slug: item.slug, utr } : { plan_slug: item.slug, utr };
      await axios.post(url, body, { headers: authHeaders(token) });
      toast.success("Submitted — admin will verify shortly");
      nav("/flatmate/dashboard");
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!item) return <div className="max-w-lg mx-auto p-8">Select a pack or plan.</div>;

  return (
    <div className="max-w-lg mx-auto px-4 md:px-8 py-10" data-testid="flatmate-pay">
      <h1 className="font-display text-3xl">Pay via UPI</h1>
      <div className="mt-4 rounded-2xl border border-black/10 bg-white p-5">
        <div className="font-display text-xl">{item.name}</div>
        <div className="text-3xl font-display mt-1" style={{ color: "#10B981" }}>₹{item.price_inr}</div>
        {item.credits && <div className="text-sm text-neutral-500">{item.credits} credits</div>}
        {item.duration_days && <div className="text-sm text-neutral-500">{item.duration_days} days validity</div>}
      </div>
      <div className="mt-4 rounded-2xl border border-black/10 bg-white p-5">
        <div className="text-sm">Pay <b>₹{item.price_inr}</b> to UPI <b>{settings.upi_id}</b></div>
        {settings.upi_qr_url && <img src={settings.upi_qr_url} className="mt-2 w-48 h-48 rounded-xl" alt="UPI QR" />}
        <form onSubmit={submit} className="mt-3 space-y-3">
          <input required placeholder="UPI transaction reference (UTR)" value={utr} onChange={(e) => setUtr(e.target.value)} className="w-full rounded-xl border border-black/10 p-3" />
          <button data-testid="pay-submit" disabled={busy} className="btn-primary rounded-full text-white px-6 py-3 font-bold w-full">
            {busy ? "Submitting…" : "Submit payment"}
          </button>
        </form>
      </div>
    </div>
  );
}

// -------------------- Dashboard --------------------
export function FlatmateDashboard() {
  const { user, token, loading } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [mine, setMine] = useState([]);
  const [journeys, setJourneys] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/flatmate/dashboard"); return; }
    axios.get(`${API}/flatmate/me`, { headers: authHeaders(token) }).then((r) => setProfile(r.data.profile));
    axios.get(`${API}/flatmate/my/listings`, { headers: authHeaders(token) }).then((r) => setMine(r.data.listings || []));
    axios.get(`${API}/flatmate/journey/mine`, { headers: authHeaders(token) }).then((r) => setJourneys(r.data.journeys || []));
  }, [user, loading, token, nav]);

  if (loading || !user) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 space-y-6" data-testid="flatmate-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl">Flatmate dashboard</h1>
          <p className="text-sm text-neutral-500">Everything for your listings, journeys & credits.</p>
        </div>
        <Link to="/flatmate/onboard" className="chip">Update preferences</Link>
      </div>

      <div className="grid md:grid-cols-4 gap-3">
        <StatMini label="Credits" value={profile?.credits ?? 0} />
        <StatMini label="Role" value={profile?.role || "—"} />
        <StatMini label="Profile" value={`${profile?.profile_completeness ?? 0}%`} />
        <Link to="/flatmate/verify" data-testid="verify-cta" className="rounded-2xl border border-black/10 bg-white p-4 hover:border-emerald-300 transition">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Verified Flatmate</div>
          <div className="font-display text-2xl mt-1 inline-flex items-center gap-1" style={{ color: profile?.verified_flatmate ? "#10B981" : "#6B7280" }}>
            {profile?.verified_flatmate ? <><BadgeCheck className="w-5 h-5" /> Yes</> : "Start"}
          </div>
        </Link>
      </div>

      <div className="rounded-2xl border border-black/10 bg-white p-5" data-testid="dashboard-journeys">
        <div className="flex justify-between items-center">
          <h2 className="font-display text-xl">My ₹99 journeys</h2>
          <Link to="/flatmate/journey" className="text-xs font-bold text-emerald-600">+ Start new</Link>
        </div>
        {journeys.length === 0 && <div className="text-sm text-neutral-500 mt-2">No journeys yet.</div>}
        <div className="space-y-2 mt-3">
          {journeys.map((j) => (
            <div key={j.id} className="rounded-xl border border-black/10 p-3 text-sm">
              <div className="flex justify-between">
                <div className="font-bold">{j.area}, {j.city}</div>
                <span className="chip text-[10px]">{j.status}</span>
              </div>
              <div className="text-xs text-neutral-500 mt-1">Budget ₹{j.budget_inr} · {new Date(j.created_at).toLocaleString()}</div>
              {j.report && <PropertyReportView report={j.report} />}
              {j.status === "report_delivered" && (
                <div className="mt-3 rounded-xl bg-emerald-50 p-3 text-xs">
                  Liked what you saw? <Link to="/flatmate/pay?plan=diy" className="font-bold underline">Get unlimited access</Link>.
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-black/10 bg-white p-5" data-testid="dashboard-listings">
        <div className="flex justify-between items-center">
          <h2 className="font-display text-xl">My listings</h2>
          <Link to="/flatmate/list" className="text-xs font-bold text-emerald-600">+ List a room</Link>
        </div>
        {mine.length === 0 && <div className="text-sm text-neutral-500 mt-2">You haven't listed a room yet.</div>}
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          {mine.map((l) => (
            <div key={l.id} className="rounded-xl border border-black/10 p-3 text-sm">
              <div className="font-bold capitalize">{l.property_type} · {l.locality}, {l.city}</div>
              <div className="text-xs text-neutral-500">₹{l.rent_inr} · {l.views} views · {l.unlocks} unlocks</div>
              <div className="mt-2 flex flex-wrap gap-1">
                {l.featured && <Badge label="Featured" color="#F97316" bg="#FEF3C7" icon={Flame} />}
                {l.trusted && <Badge label="Trusted" color="#10B981" bg="#D1FAE5" icon={Shield} />}
                {l.verified_flat && <Badge label="Verified Flat" color="#00604A" bg="#DBEAFE" icon={BadgeCheck} />}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatMini({ label, value }) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-display text-2xl mt-1" style={{ color: "#00604A" }}>{value}</div>
    </div>
  );
}

function PropertyReportView({ report }) {
  return (
    <div className="mt-3 rounded-xl bg-neutral-50 p-3 text-xs space-y-1">
      <div className="font-bold text-emerald-700">Verified Property Report</div>
      {report.overall_rating && <div>⭐ {report.overall_rating}/5</div>}
      {report.document_verification && <div><b>Docs:</b> {report.document_verification}</div>}
      {report.real_condition && <div><b>Condition:</b> {report.real_condition}</div>}
      {report.issues_found && <div><b>Issues:</b> {report.issues_found}</div>}
      {report.owner_details && <div><b>Owner:</b> {report.owner_details}</div>}
      {report.agreement_terms && <div><b>Terms:</b> {report.agreement_terms}</div>}
      {report.hidden_factors && <div><b>Hidden factors:</b> {report.hidden_factors}</div>}
      {report.maid && <div><b>Maid:</b> {report.maid}</div>}
      {report.parking && <div><b>Parking:</b> {report.parking}</div>}
      {report.locality_notes && <div><b>Locality:</b> {report.locality_notes}</div>}
      {report.extra_options && <div><b>Extra options:</b> {report.extra_options}</div>}
      {report.helper_recommendation && <div><b>Helper recommends:</b> {report.helper_recommendation}</div>}
    </div>
  );
}

// -------------------- Refund Policy --------------------
export function FlatmateRefunds() {
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-12" data-testid="refunds-page">
      <h1 className="font-display text-4xl">Refund & cancellation policy</h1>
      <p className="text-sm text-neutral-500 mt-1">Effective — HeylpMATE Flatmate Assist</p>
      <div className="mt-6 space-y-6 text-sm leading-relaxed">
        <section>
          <h2 className="font-display text-xl" style={{ color: "#10B981" }}>DIY Plan (₹449 · 30 days)</h2>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Refund eligible after 30 days if no flatmate found.</li>
            <li>Value-of-service portion only; platform fee is non-refundable.</li>
            <li>Requests must be raised within 3 months of purchase.</li>
            <li>Maximum 2 lifetime refunds per account.</li>
          </ul>
        </section>
        <section>
          <h2 className="font-display text-xl" style={{ color: "#00604A" }}>Relax Plan (₹749 · 45 days)</h2>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>50% refund after 45 days if fewer than 10 visits were arranged.</li>
            <li>Not applicable if the user did not respond to the assigned Relationship Manager.</li>
          </ul>
        </section>
        <section>
          <h2 className="font-display text-xl" style={{ color: "#F97316" }}>Credits</h2>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Non-refundable once used.</li>
            <li>Unspent credits refundable within 7 days of recharge.</li>
          </ul>
        </section>
        <section>
          <p className="text-neutral-500">All approved refunds are processed within 5 to 7 business days via the original payment method.</p>
        </section>
      </div>
    </div>
  );
}

// -------------------- Listing Photo Picker (up to 3, Emergent Object Storage) --------------------
function ListingPhotoPicker({ token, photos, onChange, max = 3 }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const API_UPLOAD = `${process.env.REACT_APP_BACKEND_URL}/api/upload/listing-photo`;

  const pick = () => inputRef.current?.click();

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const room = Math.max(0, max - (photos?.length || 0));
    if (room === 0) {
      toast.error(`Maximum ${max} photos`);
      return;
    }
    const toUpload = files.slice(0, room);
    setBusy(true);
    const next = [...(photos || [])];
    try {
      for (const f of toUpload) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await axios.post(API_UPLOAD, fd, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        next.push(r.data.path);
      }
      onChange(next);
      toast.success(`${toUpload.length} photo${toUpload.length > 1 ? "s" : ""} uploaded`);
    } catch (er) {
      toast.error(er?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = (idx) => onChange(photos.filter((_, i) => i !== idx));

  return (
    <div className="rounded-2xl border border-dashed border-black/15 bg-neutral-50/60 p-4" data-testid="listing-photo-picker">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Property photos</div>
          <div className="text-[11px] text-neutral-500">Up to {max} photos · shown on cards & detail page.</div>
        </div>
        <button type="button" onClick={pick} disabled={busy || (photos?.length || 0) >= max}
                data-testid="listing-photo-pick"
                className="btn-primary rounded-full text-white px-4 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-50">
          <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Add"}
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" />
      <div className="grid grid-cols-3 gap-2 mt-2">
        {(photos || []).map((p, i) => (
          <div key={i} className="relative rounded-xl overflow-hidden h-24 bg-black/5">
            <img src={photoUrl(p) || p} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => remove(i)}
                    data-testid={`listing-photo-remove-${i}`}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 grid place-items-center shadow">
              <Trash2 className="w-3 h-3 text-red-600" />
            </button>
          </div>
        ))}
        {(photos?.length || 0) === 0 && (
          <div className="col-span-3 text-center text-xs text-neutral-500 py-6">
            <Camera className="w-6 h-6 mx-auto text-neutral-400" />
            No photos yet — add up to {max}.
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------- Verified Flatmate — 3-step Journey --------------------
export function FlatmateVerify() {
  const { user, token, loading } = useAuth();
  const settings = useFlatmateSettings();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [selfieBusy, setSelfieBusy] = useState(false);
  const selfieRef = useRef(null);
  const [last4, setLast4] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpBusy, setOtpBusy] = useState(false);
  const [demoHint, setDemoHint] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/flatmate/verify"); return; }
    axios.get(`${API}/flatmate/me`, { headers: authHeaders(token) })
      .then((r) => setProfile(r.data.profile));
  }, [user, token, loading, nav]);

  const uploadSelfie = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setSelfieBusy(true);
    try {
      const path = await uploadPhoto(f, token);
      await axios.post(`${API}/flatmate/me`, { selfie_path: path }, { headers: authHeaders(token) });
      toast.success("Selfie saved");
      const r = await axios.get(`${API}/flatmate/me`, { headers: authHeaders(token) });
      setProfile(r.data.profile);
    } catch (er) { toast.error(er?.response?.data?.detail || "Upload failed"); }
    finally { setSelfieBusy(false); e.target.value = ""; }
  };

  const sendOtp = async () => {
    if (!/^\d{4}$/.test(last4) && !/^\d{12}$/.test(last4)) return toast.error("Enter Aadhaar last-4 (demo) or full 12 digits");
    setOtpBusy(true);
    try {
      const body = last4.length === 12 ? { aadhaar: last4 } : { aadhaar_last4: last4 };
      const r = await axios.post(`${API}/flatmate/aadhaar-otp/send`, body,
        { headers: authHeaders(token) });
      setOtpSent(true);
      setDemoHint(r.data.hint || "");
      toast.success(r.data.demo ? "OTP sent (DEMO)" : "OTP sent to your Aadhaar-linked mobile");
    } catch (er) { toast.error(er?.response?.data?.detail || "Failed"); }
    finally { setOtpBusy(false); }
  };

  const verifyOtp = async () => {
    if (!/^\d{6}$/.test(otp)) return toast.error("Enter the 6-digit OTP");
    setOtpBusy(true);
    try {
      const r = await axios.post(`${API}/flatmate/aadhaar-otp/verify`, { otp },
        { headers: authHeaders(token) });
      toast.success(r.data.verified_flatmate ? "Verified Flatmate badge unlocked! 🎉" : "Aadhaar mobile verified");
      const p = await axios.get(`${API}/flatmate/me`, { headers: authHeaders(token) });
      setProfile(p.data.profile);
    } catch (er) { toast.error(er?.response?.data?.detail || "Invalid OTP"); }
    finally { setOtpBusy(false); }
  };

  if (!profile) return <div className="max-w-2xl mx-auto p-8 text-neutral-500">Loading…</div>;

  const aadhaarProvider = settings?.aadhaar_provider || "demo";
  const mobileOk = !!(user && user.verified);
  const selfieOk = !!profile.selfie_path;
  const aadhaarOk = !!profile.aadhaar_mobile_verified;
  const completeness = profile.profile_completeness || 0;
  const allDone = selfieOk && mobileOk && aadhaarOk && completeness >= 80;

  const Step = ({ n, done, title, desc, children }) => (
    <div className={`rounded-2xl border p-5 ${done ? "border-emerald-300 bg-emerald-50/50" : "border-black/10 bg-white"}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-full grid place-items-center text-white font-bold flex-none ${done ? "bg-emerald-500" : "bg-neutral-400"}`}>
          {done ? <CheckCircle2 className="w-5 h-5" /> : n}
        </div>
        <div className="flex-1">
          <div className="font-display text-lg">{title}</div>
          <div className="text-xs text-neutral-500 mt-0.5">{desc}</div>
          {children}
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-10 space-y-4" data-testid="flatmate-verify">
      <div>
        <div className="chip mb-2" style={{ borderColor: "#10B981", color: "#10B981" }}>
          <BadgeCheck className="w-3.5 h-3.5" /> Verified Flatmate journey
        </div>
        <h1 className="font-display text-3xl">Earn the green badge</h1>
        <p className="text-sm text-neutral-500 mt-1">Complete all 3 checks + fill your profile to at least 80%. Listers and seekers both trust this badge.</p>
        <div className="mt-3 h-2 rounded-full bg-neutral-100 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${completeness}%` }} />
        </div>
        <div className="text-[11px] text-neutral-500 mt-1">Profile completeness: {completeness}%</div>
      </div>

      <Step n={1} done={selfieOk} title="Selfie at registration"
            desc="A clear face selfie helps us mark you as a real person.">
        {selfieOk ? (
          <div className="mt-3 flex items-center gap-3">
            <img src={photoUrl(profile.selfie_path)} alt="selfie" className="w-16 h-16 rounded-full object-cover border-2 border-emerald-400" />
            <button onClick={() => selfieRef.current?.click()} data-testid="selfie-replace" className="text-xs text-emerald-700 underline">Replace</button>
          </div>
        ) : (
          <button onClick={() => selfieRef.current?.click()} disabled={selfieBusy}
                  data-testid="selfie-upload-btn"
                  className="mt-3 btn-primary rounded-full text-white px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50">
            <Camera className="w-4 h-4" /> {selfieBusy ? "Uploading…" : "Take / upload selfie"}
          </button>
        )}
        <input ref={selfieRef} type="file" accept="image/*" onChange={uploadSelfie} className="hidden" />
      </Step>

      <Step n={2} done={mobileOk} title="Mobile OTP confirmed"
            desc="Confirms your primary contact number on the platform.">
        {mobileOk ? (
          <div className="text-xs text-emerald-700 mt-2">✓ Mobile verified on your HeylpMATE account.</div>
        ) : (
          <Link to="/register?next=/flatmate/verify" data-testid="mobile-otp-link"
                className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-emerald-700">
            <Phone className="w-4 h-4" /> Verify mobile on my account
          </Link>
        )}
      </Step>

      <Step n={3} done={aadhaarOk} title="Aadhaar-linked mobile OTP (optional for now)"
            desc="We'll link your UPI mobile with Aadhaar records later — admin will finalise after onboarding.">
        {aadhaarProvider === "surepass" ? (
          <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest rounded-full px-2 py-0.5"
               style={{ background: "#D1FAE5", color: "#065F46" }}>
            <Fingerprint className="w-3 h-3" /> SurePass Verified
          </div>
        ) : (
          <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest rounded-full px-2 py-0.5"
               style={{ background: "#FEF3C7", color: "#B45309" }}>
            <Fingerprint className="w-3 h-3" /> Optional · admin verifies later
          </div>
        )}
        {aadhaarOk ? (
          <div className="text-xs text-emerald-700 mt-3">
            ✓ Aadhaar-linked mobile OTP verified (last 4: •••• {profile.aadhaar_last4}).
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            {!otpSent ? (
              <div className="flex gap-2">
                <input inputMode="numeric" maxLength={12} placeholder="Aadhaar (last-4 for demo, or full 12)"
                       data-testid="aadhaar-last4"
                       value={last4} onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 12))}
                       className="flex-1 rounded-xl border border-black/10 p-2 text-sm font-mono" />
                <button onClick={sendOtp} disabled={otpBusy || (last4.length !== 4 && last4.length !== 12)}
                        data-testid="aadhaar-send-otp"
                        className="btn-primary rounded-full text-white px-4 py-2 text-sm disabled:opacity-50">
                  {otpBusy ? "…" : "Send OTP"}
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {demoHint && (
                  <div className="text-[11px] rounded-lg bg-amber-50 border border-amber-200 text-amber-800 p-2">
                    <b>DEMO:</b> {demoHint}
                  </div>
                )}
                <div className="flex gap-2">
                  <input inputMode="numeric" maxLength={6} placeholder="6-digit OTP"
                         data-testid="aadhaar-otp"
                         value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                         className="w-40 rounded-xl border border-black/10 p-2 text-sm font-mono" />
                  <button onClick={verifyOtp} disabled={otpBusy || otp.length !== 6}
                          data-testid="aadhaar-verify-otp"
                          className="btn-primary rounded-full text-white px-4 py-2 text-sm disabled:opacity-50">
                    {otpBusy ? "…" : "Verify"}
                  </button>
                </div>
                <button onClick={() => { setOtpSent(false); setOtp(""); }} className="text-[11px] text-neutral-500 underline">
                  Change Aadhaar last 4
                </button>
              </div>
            )}
          </div>
        )}
      </Step>

      <div className={`rounded-2xl p-5 ${allDone ? "bg-emerald-500 text-white" : "bg-neutral-50 text-neutral-600"}`}>
        <div className="flex items-center gap-2">
          <BadgeCheck className={`w-5 h-5 ${allDone ? "" : "opacity-40"}`} />
          <div className="font-display text-lg">
            {allDone ? "You are a Verified Flatmate 🎉" : "Complete all 3 steps to earn the badge"}
          </div>
        </div>
        {allDone && (
          <div className="text-sm mt-1 opacity-90">Your listings and profile now show the green Verified Flatmate badge.</div>
        )}
      </div>
    </div>
  );
}

